import React, { useState, useEffect, useCallback } from 'react';
import { Upload, message } from 'antd';
import { PlusOutlined, LoadingOutlined } from '@ant-design/icons';
import api from '../utils/api';

const ImageUpload = ({
  value,
  onChange,
  maxCount = 1,
  accept = 'image/*',
  listType = 'picture-card',
  sizeLimit = 5, // MB
  placeholder = '上传图片',
  disabled = false,
  onUploadSuccess, // 上传成功回调
  ...props
}) => {
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);
  
  // 当 value 变化时更新 fileList
  useEffect(() => {
    if (value && typeof value === 'string') {
      // 如果 value 是字符串 URL，创建文件对象
      const newFileList = [
        {
          uid: '-1',
          name: 'image.png',
          status: 'done',
          url: value,
        },
      ];
      setFileList(newFileList);
    } else if (Array.isArray(value) && value.length > 0) {
      // 如果 value 是数组，直接使用
      setFileList(value);
    } else if (value && typeof value === 'object' && value.url) {
      // 如果 value 是包含 url 属性的对象
      const newFileList = [
        {
          uid: value.uid || '-1',
          name: value.name || 'image.png',
          status: value.status || 'done',
          url: value.url,
        },
      ];
      setFileList(newFileList);
    } else {
      // 如果没有值，清空文件列表
      setFileList([]);
    }
  }, [value]);

  // 上传前检查
  const beforeUpload = (file) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('只能上传图片文件');
      return Upload.LIST_IGNORE;
    }
    
    const isLtSize = file.size / 1024 / 1024 < sizeLimit;
    if (!isLtSize) {
      message.error(`图片大小不能超过 ${sizeLimit}MB`);
      return Upload.LIST_IGNORE;
    }
    
    return true; // 允许上传
  };

  // 自定义上传请求
  const customRequest = async ({ file, onSuccess, onError, onProgress }) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', 'general');

    try {
      setUploading(true);
      const response = await api.post('/common/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const { loaded, total } = progressEvent;
          const percent = Math.round((loaded / total) * 100);
          onProgress({ percent });
        },
      });
      
      // api拦截器返回的是 {code: 200, data: {fileUrl, fileName, ...}, message: '...'}
      // 所以 response 就是 {code: 200, data: {fileUrl, fileName, ...}, message: '...'}
      // response.data 就是 {fileUrl, fileName, ...}
      const fileUrl = response.data?.fileUrl;
      
      if (fileUrl && typeof fileUrl === 'string') {
        // 立即更新表单字段值
        if (onChange) {
          onChange(fileUrl);
        }
        // 如果提供了onUploadSuccess回调，也调用它
        if (onUploadSuccess) {
          onUploadSuccess(fileUrl);
        }
        // 将完整的响应数据传递给onSuccess，以便在handleChange中获取
        onSuccess({ data: response.data }, file);
      } else {
        throw new Error('上传失败：响应格式错误');
      }
    } catch (error) {
      onError(error);
    } finally {
      setUploading(false);
    }
  };

  // 处理文件变化
  const handleChange = useCallback(({ file, fileList }) => {
    // 限制文件数量
    const limitedFileList = fileList.slice(-maxCount);
    setFileList(limitedFileList);

    if (file.status === 'done') {
      // 从响应中获取fileUrl
      const fileUrl = file.response?.data?.fileUrl;
      if (fileUrl && typeof fileUrl === 'string') {
        message.success('上传成功');
        // 确保表单值被更新（customRequest中已经调用过onChange，这里再次确保）
        if (onChange) {
          onChange(fileUrl);
        }
        // 如果提供了onUploadSuccess回调，也调用它
        if (onUploadSuccess) {
          onUploadSuccess(fileUrl);
        }
      } else {
        message.error('上传失败：响应格式错误');
        if (onChange) {
          onChange(null);
        }
      }
    } else if (file.status === 'error') {
      message.error('上传失败');
      // 上传失败时也通知表单组件
      if (onChange) {
        onChange(null);
      }
    } else if (file.status === 'removed') {
      // 文件被移除时，清空表单值
      if (onChange) {
        onChange(null);
      }
    }
  }, [maxCount, onChange]);

  // 处理文件移除
  const handleRemove = () => {
    setFileList([]);
    if (onChange) {
      onChange(null);
    }
    return true;
  };

  const uploadButton = (
    <div>
      {uploading ? <LoadingOutlined /> : <PlusOutlined />}
      <div style={{ marginTop: 8 }}>{placeholder}</div>
    </div>
  );

  return (
    <Upload
      accept={accept}
      listType={listType}
      fileList={fileList}
      maxCount={maxCount}
      beforeUpload={beforeUpload}
      customRequest={customRequest}
      onChange={handleChange}
      onRemove={handleRemove}
      disabled={disabled || uploading}
      {...props}
    >
      {fileList.length >= maxCount ? null : uploadButton}
    </Upload>
  );
};

export default ImageUpload;